/**
 * Pure helpers for reconciliation-v2 components.
 *
 * Extracted so they can be unit-tested in isolation (no React/API imports).
 */

import {
  AllocationClassification,
  ExplicitCreditReason,
  PULL_DAY_NOT_SET,
  PullDayFilter,
  ReconIssueType,
  RefundReason,
} from '../../api/reconciliation-v2-enums'

// Re-exported so existing consumers importing these from this helpers module
// keep resolving.
export { ExplicitCreditReason, ReconIssueType }
export type { RefundReason }

// ── split-evenly math ────────────────────────────────────────────────────────

export type GroupRows = Record<string, string>

/**
 * Divide `sourceAmount` evenly across `periods`, rounding to cents. The first
 * period absorbs any rounding residual so the total is exact.
 *
 * Returns `null` if there are no periods or `sourceAmount <= 0`.
 */
export function splitEvenlyRows(
  periods: string[],
  sourceAmount: number
): GroupRows | null {
  if (periods.length === 0 || sourceAmount <= 0) return null
  const each = Math.floor((sourceAmount / periods.length) * 100) / 100
  const first = +(sourceAmount - each * (periods.length - 1)).toFixed(2)
  const rows: GroupRows = {}
  periods.forEach((p, i) => {
    rows[p] = (i === 0 ? first : each).toFixed(2)
  })
  return rows
}

// ── refund classification resolution ────────────────────────────────────────

export const REFUND_REASON_LABELS: Record<RefundReason, string> = {
  [AllocationClassification.REFUND_CANCELLATION]: 'Cancellation',
  [AllocationClassification.REFUND_DEPENDENT_REMOVAL]: 'Dependent removal',
  [AllocationClassification.REFUND_BILLING_CORRECTION]: 'Billing correction',
  [AllocationClassification.REFUND_OTHER]: 'Other',
}

export const REFUND_REASONS: RefundReason[] = [
  AllocationClassification.REFUND_CANCELLATION,
  AllocationClassification.REFUND_DEPENDENT_REMOVAL,
  AllocationClassification.REFUND_BILLING_CORRECTION,
  AllocationClassification.REFUND_OTHER,
]

const REFUND_REASONS_SET = new Set<string>(REFUND_REASONS)

/** True when the value is a refund-specific classification (not CHARGE). */
export function isRefundReason(
  v: string | null | undefined
): v is RefundReason {
  return !!v && REFUND_REASONS_SET.has(v)
}

/**
 * Resolve the `classification` that should be placed on an allocate-batch
 * slice.
 *
 * Rules:
 * - Positive slice (sourceSign >= 0): use `sourceClassification` (preserves
 *   an existing charge/refund on cell-mode edits) or `undefined` for fresh
 *   allocations.
 * - Negative/refund slice: `pickedReason` takes priority (operator's explicit
 *   choice), then `sourceClassification` if it's already a refund value, then
 *   `'REFUND_OTHER'` as the ultimate default.
 */
export function resolveSliceClassification(
  sourceSign: number,
  sourceClassification: AllocationClassification | null,
  pickedReason: RefundReason
): AllocationClassification | undefined {
  if (sourceSign >= 0) {
    return sourceClassification ?? undefined
  }
  return pickedReason
}

// ── set-expected-price helpers ───────────────────────────────────────────────

/**
 * Normalize a dollar-amount string entered by the operator into the decimal
 * string the backend expects.
 *
 * Accepts any string that `Number()` can parse to a finite, non-negative value.
 * Returns a two-decimal string (e.g. "123.45") on success, or `null` when the
 * input is blank, non-numeric, or negative.
 *
 * The returned string always satisfies the backend regex: /^-?\d+(\.\d{1,2})?$/
 */
export function normalizeExpectedAmount(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  return n.toFixed(2)
}

export interface SetExpectedRequestInput {
  benefitId: string
  periodStartDate: string
  /** Raw dollar string from the form input (e.g. "123.45" or "123"). */
  rawAmount: string
  note?: string
}

export interface SetExpectedRequestOutput {
  benefitId: string
  periodStartDate: string
  expected: string
  note?: string
}

/**
 * Build the POST body for the set-expected-price endpoint.
 *
 * Returns `null` when the amount cannot be normalized (invalid input guard).
 *
 * Callers should treat a `null` return as a validation failure and prevent
 * form submission.
 */
export function buildSetExpectedRequest(
  input: SetExpectedRequestInput
): SetExpectedRequestOutput | null {
  const expected = normalizeExpectedAmount(input.rawAmount)
  if (expected === null) return null
  const out: SetExpectedRequestOutput = {
    benefitId: input.benefitId,
    periodStartDate: input.periodStartDate,
    expected,
  }
  if (input.note) out.note = input.note
  return out
}

// ── explicit-credit helpers ──────────────────────────────────────────────────

/** Reasons offered in the Explicit credit section, in display order. */
export const EXPLICIT_CREDIT_REASONS: ExplicitCreditReason[] = [
  ExplicitCreditReason.CARRIER_REBATE,
  ExplicitCreditReason.PREMIUM_CORRECTION,
  ExplicitCreditReason.BILLED_TWICE,
  ExplicitCreditReason.OTHER,
]

export const EXPLICIT_CREDIT_REASON_LABELS: Record<
  ExplicitCreditReason,
  string
> = {
  [ExplicitCreditReason.CARRIER_REBATE]: 'Carrier rebate',
  [ExplicitCreditReason.PREMIUM_CORRECTION]: 'Premium correction',
  [ExplicitCreditReason.BILLED_TWICE]: 'Billed twice',
  [ExplicitCreditReason.OTHER]: 'Other',
}

export interface RecordExplicitCreditInput {
  benefitId: string
  periodStartDate: string
  /** Raw dollar string from the form input (e.g. "100" or "100.00"). */
  rawAmount: string
  /** Operator's pick; `null` until they choose one. */
  reason: ExplicitCreditReason | null
  note?: string
}

export interface RecordExplicitCreditOutput {
  benefitId: string
  periodStartDate: string
  /** Positive two-decimal dollar string; the api-client converts it to cents. */
  amount: string
  reason: ExplicitCreditReason
  note?: string
}

/**
 * Build the POST body for the record-explicit-credit endpoint.
 *
 * Returns `null` when the form is not submittable: missing benefit/period,
 * missing reason, or an amount that isn't a positive number. The backend
 * requires `amountCents >= 1`, so a zero amount is rejected here too rather
 * than round-tripping for a 400.
 *
 * Callers treat a `null` return as a validation failure and block submission.
 */
export function buildRecordExplicitCreditRequest(
  input: RecordExplicitCreditInput
): RecordExplicitCreditOutput | null {
  if (!input.benefitId || !input.periodStartDate) return null
  if (!input.reason) return null
  const amount = normalizeExpectedAmount(input.rawAmount)
  if (amount === null || Number(amount) <= 0) return null
  const out: RecordExplicitCreditOutput = {
    benefitId: input.benefitId,
    periodStartDate: input.periodStartDate,
    amount,
    reason: input.reason,
  }
  const note = input.note?.trim()
  if (note) out.note = note
  return out
}

/**
 * Paid-vs-owed tolerance, in DOLLARS. Mirrors the backend's 8-cent threshold —
 * `ISSUE_THRESHOLD_CENTS` in `issue-materializer.service.ts:23` and `TOLERANCE`
 * in `reconciliation-v2/utils/reconciliation-math.ts:11`. MUST stay in lockstep
 * with both: the server decides whether a cell is an issue, and this decides
 * whether the drawer prints a shortfall, so a divergence makes the month strip
 * and the Adjust drawer disagree about the same benefit-month.
 */
export const ISSUE_TOLERANCE_DOLLARS = 0.08

/** Round a dollar subtraction back onto whole cents. */
const roundCents = (n: number): number => Math.round(n * 100) / 100

export interface PeriodMoneySummaryInput {
  /** Net dollars allocated into the benefit-month. */
  totalPaid: number
  /** What the member was invoiced on; null when none resolves. */
  expected: number | null
  /**
   * Positive dollar magnitudes of the explicit credits on the period — the
   * drawer's own credit rows. Their sum IS the credited total.
   */
  creditAmounts: number[]
  /**
   * Server-computed expected-less-credits, authoritative when present. Null or
   * absent when no expected resolves server-side, or when a response predates
   * the field; the credited sum then derives it from `expected` instead, so a
   * period that plainly has credit rows never prints as uncredited.
   */
  netDue?: number | null
}

export interface PeriodMoneySummary {
  expected: number | null
  /** Sum of the period's credits. 0 when uncredited. */
  credited: number
  /**
   * How many credit rows make up `credited`. The drawer's panel labels the line
   * "Explicit credit (3)" once more than one row is behind the figure, so the
   * operator knows the number is a sum before scrolling to the list.
   *
   * Meaningful only where the caller holds the period's actual rows — the month
   * strip passes the server's batched SUM as a single synthetic amount, so its
   * count is 1 for any credited month and is deliberately not displayed there.
   */
  creditCount: number
  /** What the member still owes; null when no expected resolves. */
  netDue: number | null
  /** How far paid falls below net due, past the tolerance. 0 when covered. */
  shortfall: number
  /** True when the period carries any credit — gates the panel's credit line. */
  hasCredit: boolean
}

/**
 * The Adjust drawer's left summary panel arithmetic.
 *
 * The credited total is the sum of the period's own credit rows — the same rows
 * listed a few pixels away in the Explicit credit section — so the printed
 * figure can never disagree with the list the operator is reading. Net due is
 * taken from the server when it sent one (it derives issues from that exact
 * number), and derived from `expected` less the credits only as a fallback.
 *
 * Shortfall is one-directional: an overpayment is Absorb's business, not this
 * panel's, and a gap inside the issue tolerance is not a gap at all.
 */
export function computePeriodMoneySummary(
  input: PeriodMoneySummaryInput
): PeriodMoneySummary {
  const { totalPaid, expected, creditAmounts } = input
  const credited = roundCents(creditAmounts.reduce((sum, a) => sum + a, 0))
  const netDue =
    input.netDue ?? (expected == null ? null : roundCents(expected - credited))
  const gap = netDue == null ? 0 : roundCents(netDue - totalPaid)
  return {
    expected,
    credited,
    creditCount: creditAmounts.length,
    netDue,
    shortfall: gap > ISSUE_TOLERANCE_DOLLARS ? gap : 0,
    hasCredit: credited > 0,
  }
}

// ── cell refund summary ──────────────────────────────────────────────────────

export interface CellRefundSummary {
  hasRefund: boolean
  /** Number of refund slices in the cell. */
  refundCount: number
  /** Sum of the refund slices — negative dollars (0 when none). */
  refundTotal: number
}

/**
 * Refund slices in a benefit-month cell. A slice is a refund when its signed
 * amount is negative — EPS stores refund allocations negative (rail refunds and
 * manual Check / Group ACH refunds alike), so the sign is the signal. A cell
 * whose charges outweigh its refunds still reports them.
 */
export function summarizeCellRefunds(
  payments: ReadonlyArray<{ amount: string }>
): CellRefundSummary {
  const refunds = payments
    .map((p) => Number(p.amount))
    .filter((n) => Number.isFinite(n) && n < 0)
  return {
    hasRefund: refunds.length > 0,
    refundCount: refunds.length,
    refundTotal: roundCents(refunds.reduce((sum, n) => sum + n, 0)),
  }
}

// ── manual flag helpers ──────────────────────────────────────────────────────

export interface FlagIssueRequestInput {
  benefitId: string
  periodStartDate: string
  note?: string
}

export interface FlagIssueRequestOutput {
  benefitId: string
  /** YYYY-MM-DD first day of the affected month. */
  periodStartDate: string
  note?: string
}

/**
 * Build the POST body for flag-issue / unflag-issue — validates that both
 * required fields are non-empty strings before returning.
 *
 * Returns `null` when either `benefitId` or `periodStartDate` is missing,
 * signalling a caller-side bug (not a user input error).
 */
export function buildFlagRequest(
  input: FlagIssueRequestInput
): FlagIssueRequestOutput | null {
  if (!input.benefitId || !input.periodStartDate) return null
  const out: FlagIssueRequestOutput = {
    benefitId: input.benefitId,
    periodStartDate: input.periodStartDate,
  }
  if (input.note) out.note = input.note
  return out
}

/**
 * Decide which API call to make based on the current manual-flag state.
 *
 * Returns `'flag'` when the cell is not currently manually flagged (clicking
 * will add the flag) and `'unflag'` when it already is.
 */
export function decideFlagAction(
  isManuallyFlagged: boolean
): 'flag' | 'unflag' {
  return isManuallyFlagged ? 'unflag' : 'flag'
}

// ── manual_review label / tone map ───────────────────────────────────────────

/**
 * Operator-facing name of each issue type. `manual_review` reads "Flagged
 * issue": it is the type the modal's "Flag as issue" button creates, so the
 * label now names it by the action that made it rather than by the wire enum.
 * That also settles spec D11's "review" ban for this label, which used to be
 * exempted on the grounds of being the wire name.
 */
export const ISSUE_TYPE_LABEL: Record<ReconIssueType, string> = {
  [ReconIssueType.Overpaid]: 'Overpaid',
  [ReconIssueType.Underpaid]: 'Underpaid',
  [ReconIssueType.ManualReview]: 'Flagged issue',
}

export const ISSUE_TYPE_TONE: Record<
  ReconIssueType,
  'warning' | 'danger' | 'primary'
> = {
  [ReconIssueType.Overpaid]: 'warning',
  [ReconIssueType.Underpaid]: 'danger',
  [ReconIssueType.ManualReview]: 'primary',
}

// ── the absorption note ──────────────────────────────────────────────────────
//
// An absorption is never UPDATEd: `allocateBatch` hard-deletes every absorption
// on the transaction and re-records it from the request body, and the server
// carries nothing forward. So the client must send a note on EVERY absorb
// submit or it silently becomes NULL. The contract keeps `note` optional (old
// absorptions stay valid); the "you must justify a write-off" rule is finance
// policy enforced here, at the operator. See enrollment-platform ADR 0003.

/** Mirrors the backend's `z.string().trim().max(500)` on every absorb request. */
export const ABSORPTION_NOTE_MAX_LENGTH = 500

/**
 * The gate every absorb button shares: a write-off needs a non-empty
 * justification that fits the contract's length cap.
 */
export function isAbsorptionNoteValid(note: string): boolean {
  const trimmed = note.trim()
  return trimmed.length > 0 && trimmed.length <= ABSORPTION_NOTE_MAX_LENGTH
}

// ── unresolved benefits ──────────────────────────────────────────────────────
//
// recon-v2 reads benefits through one filtered projection (active, carrier
// present, non-Medicare). An allocation outlives that filter: deactivate a
// benefit upstream — or drop it from the sync — and the money stays in
// period_allocation while the benefit becomes invisible. The server then has no
// name to send and falls back to the bare id, which reads as a member name in
// every place a label belongs. `benefitResolved: false` is it saying so.

/** What stands in for a name when the benefit behind an allocation is gone. */
export const UNRESOLVED_BENEFIT_LABEL = 'Unresolved benefit'

/**
 * The label to show where a benefit's NAME belongs. Only an explicit `false`
 * counts as unresolved: a server that predates the flag sends `undefined`, and
 * that must keep rendering the label it did before rather than marking every
 * allocation unresolved.
 */
export function benefitNameLabel(input: {
  benefitId: string
  benefitLabel?: string | null
  benefitResolved?: boolean
}): string {
  if (input.benefitResolved === false) return UNRESOLVED_BENEFIT_LABEL
  return input.benefitLabel || input.benefitId
}
/**
 * Copy for the application deep link — the control that opens a benefit's own
 * application page (`/application-manager/:benefitId`).
 *
 * It says "App Engine" because that is what operators call that screen, and
 * because Payment assignment's link to the SAME destination is already labelled
 * "App Engine Link" (`PaymentDetailModal/EmployeeInformationSection.tsx`).
 *
 * The nav's own label for this route is the bare "Application engine"
 * (`SideNav/link-data.js:64-68`) — correct, and avoided anyway: the nav gives
 * `/application-engine`, a different screen, "Application engine V2", and the
 * unqualified phrase reads as that grid to anyone who has used it. Shipping a
 * link to that grid on the strength of the phrase is exactly what this ticket
 * had to reverse (ADR-0003). Do not "correct" this to the nav's wording.
 *
 * Here rather than inline so the three placements — the Benefits tab's Insured
 * cell, the Adjust drawer's rail, and the Unallocated payments row — cannot
 * word the same control differently.
 */
export const OPEN_IN_APP_ENGINE_LABEL = 'Open in App Engine'

// ── autopay pull date ────────────────────────────────────────────────────────

const ORDINAL_SUFFIX: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' }

/** A pull day of month as an ordinal: 1 → "1st", 22 → "22nd", 13 → "13th". */
export const formatPullDay = (day: number): string => {
  const lastTwo = day % 100
  if (lastTwo >= 11 && lastTwo <= 13) return `${day}th`
  return `${day}${ORDINAL_SUFFIX[day % 10] ?? 'th'}`
}

/** What a benefit with no pull date reads as, in the table and the filter. */
export const PULL_DAY_NOT_SET_LABEL = 'Not set'

/**
 * The pull-date editor's options: every day 1–31. No "Not set" — EPS accepts
 * only 1–31, so a pull date can be changed but not cleared.
 */
export const PULL_DAY_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 31 },
  (_, i) => ({
    value: i + 1,
    label: formatPullDay(i + 1),
  })
)

/** The Benefits pull-day filter's options: every day 1–31, then Not set. */
export const PULL_DAY_FILTER_OPTIONS: {
  value: PullDayFilter
  label: string
}[] = [
  ...PULL_DAY_OPTIONS,
  { value: PULL_DAY_NOT_SET, label: PULL_DAY_NOT_SET_LABEL },
]
